import { t } from '@lingui/macro'
import { format } from 'date-fns'
import { useState } from 'react'
import { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import {
  getMedicationDosageText,
  getMedicationEmptyMessage,
  getMedicationPhotoFileIds,
  isMedicationFileProcessed,
} from '@/modules/app/utils/medication.utils'
import { useStoredMedicationFiles } from '@/modules/questionnaire/hooks/useStoredMedicationFiles'
import { ImagePreviewModal } from '@/modules/shared/components/image-preview-modal/ImagePreviewModal'
import { MedicationList } from '@/modules/shared/components/medication-list/MedicationList'
import { MedicationPhotoBadge } from './MedicationPhotoBadge'
import { MedicationPhotoFiles } from './MedicationPhotoFiles'
import { MedicationStoredPhoto } from './MedicationStoredPhoto'

import type {
  MedicationFileAttachment,
  MedicationSchemeStatus,
  MedicationStatement,
} from '@/modules/app/types/HealthParameter.types'

type MedicationSchemeOverviewProps = {
  medications: MedicationStatement[]
  schemeStatus?: MedicationSchemeStatus
  files?: MedicationFileAttachment[]
}

/**
 * A submitted medication scheme, read only: every line with the photo it was
 * read from, and the photos that produced no line. Tapping a photo opens it
 * full screen.
 */
export const MedicationSchemeOverview = ({ medications, schemeStatus, files = [] }: MedicationSchemeOverviewProps) => {
  const theme = useTheme()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  // TODO-v10: a domain component should not fetch. Fetch the photo urls where
  // the scheme is loaded (the profile section, the task detail) and hand them in.
  const storedFiles = useStoredMedicationFiles(getMedicationPhotoFileIds(medications, files))

  const photoAlt = t({
    id: 'medication.list.photoAlt',
    message: 'Foto van je medicatie',
  })

  const fileProcessedText = t({
    id: 'medication.file.processed',
    comment: 'Shown in place of a medication file the care team processed and removed',
    message: 'Bestand verwerkt in je dossier',
  })

  const isProcessed = (fileId: string) => isMedicationFileProcessed(storedFiles.get(fileId))

  return (
    <FlexColumn gap={theme.UI.Spacing.Space2}>
      {/* Photos alone are a scheme too, so the empty message only shows without them. */}
      {medications.length === 0 && files.length === 0 && (
        <MedicationList.Item>{getMedicationEmptyMessage(schemeStatus)}</MedicationList.Item>
      )}

      {medications.length > 0 && (
        <MedicationList>
          {medications.map((medication, index) => {
            const dosageText = getMedicationDosageText(medication)

            return (
              <MedicationList.Item key={`${medication.name}-${index}`}>
                <MedicationList.Name>{medication.name}</MedicationList.Name>
                {dosageText && <MedicationList.Dosage>{dosageText}</MedicationList.Dosage>}
                {medication.attachment && (
                  <MedicationPhotoBadge>
                    <MedicationStoredPhoto
                      file={storedFiles.get(medication.attachment.fileId)}
                      alt={photoAlt}
                      onOpen={setPreviewUrl}
                    />
                    {isProcessed(medication.attachment.fileId) ? (
                      <MedicationPhotoBadge.Text>{fileProcessedText}</MedicationPhotoBadge.Text>
                    ) : (
                      <MedicationPhotoBadge.Content>
                        <MedicationPhotoBadge.Icon icon={['fas', 'sparkle']} />
                        <MedicationPhotoBadge.Label>
                          {t({
                            id: 'medication.list.readFromPhoto',
                            message: 'Met AI uit je foto gelezen',
                          })}
                        </MedicationPhotoBadge.Label>
                      </MedicationPhotoBadge.Content>
                    )}
                  </MedicationPhotoBadge>
                )}
              </MedicationList.Item>
            )
          })}
        </MedicationList>
      )}

      {files.length > 0 && (
        <MedicationPhotoFiles>
          <MedicationPhotoFiles.Title>
            {t({
              id: 'medication.photoFiles.title',
              message: "Foto's en bestanden",
            })}
          </MedicationPhotoFiles.Title>

          {files.map((photo) => (
            <MedicationPhotoFiles.Item key={photo.fileId}>
              <MedicationStoredPhoto file={storedFiles.get(photo.fileId)} alt={photoAlt} onOpen={setPreviewUrl} />

              <FlexColumn>
                <MedicationPhotoFiles.Name>
                  {isProcessed(photo.fileId)
                    ? fileProcessedText
                    : t({
                        id: 'medication.photoFiles.name',
                        message: 'Foto van je medicatie',
                      })}
                </MedicationPhotoFiles.Name>
                <MedicationPhotoFiles.Timestamp>
                  {format(new Date(photo.takenAt), 'dd/MM/yyyy HH:mm')}
                </MedicationPhotoFiles.Timestamp>
              </FlexColumn>
            </MedicationPhotoFiles.Item>
          ))}

          <MedicationPhotoFiles.Note>
            {t({
              id: 'medication.photoFiles.note',
              message: 'Deze gaan mee naar je opnamedossier zodat je zorgteam deze kan verwerken.',
            })}
          </MedicationPhotoFiles.Note>
        </MedicationPhotoFiles>
      )}

      <ImagePreviewModal isVisible={!!previewUrl} onClose={() => setPreviewUrl(null)}>
        <ImagePreviewModal.Spinner />
        <ImagePreviewModal.Image src={previewUrl ?? undefined} alt={photoAlt} />
        <ImagePreviewModal.CloseButton onClick={() => setPreviewUrl(null)} />
      </ImagePreviewModal>
    </FlexColumn>
  )
}
